import React, { useState } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    howFarAlong: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (errorMessage) setErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await fetch('/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          howFarAlong: '',
          message: '',
        });
        // Hide success message after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage('Unable to send message. Please email us directly at contact@soulbridgedoula.co.uk');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f4f0]">
      {/* Contact Section - moved up without hero */}
      <section className="py-12 px-4 bg-[#f5f4f0]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
          {/* Contact Form - Now on LEFT */}
          <div 
            className="bg-white p-8 rounded-2xl"
            style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 8px 24px rgba(0, 0, 0, 0.06)' }}
          >
            <h2 className="text-2xl font-semibold mb-6" style={{ color: '#654F54' }}>Send Me a Message</h2>
            
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800 text-sm">{errorMessage}</p>
              </div>
            )}

            {isSubmitted ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <p className="text-green-800 font-semibold mb-2">Thank you for reaching out!</p>
                <p className="text-green-600 text-sm">
                  I'll get back to you within 24-48 hours. A copy of your message has been sent to your email.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#815863] focus:border-transparent transition-all"
                    placeholder="Jane Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#815863] focus:border-transparent transition-all"
                    placeholder="jane@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium mb-2">
                    Phone Number (optional)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#815863] focus:border-transparent transition-all"
                    placeholder="+44 123 456 7890"
                  />
                </div>

                <div>
                  <label htmlFor="location" className="block text-sm font-medium mb-2">
                    Where are you based?
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#815863] focus:border-transparent transition-all"
                    placeholder="e.g., Surrey, London"
                  />
                </div>

                <div>
                  <label htmlFor="howFarAlong" className="block text-sm font-medium mb-2">
                    How far along are you? (optional)
                  </label>
                  <input
                    type="text"
                    id="howFarAlong"
                    name="howFarAlong"
                    value={formData.howFarAlong}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#815863] focus:border-transparent transition-all"
                    placeholder="e.g., 20 weeks, or postpartum"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2">
                    How can I support you today?
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#815863] focus:border-transparent transition-all resize-none"
                    placeholder="Tell me a bit about yourself and what you're looking for..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#815863] text-white px-8 py-3 rounded-full tracking-wider text-sm hover:bg-[#4d6654] transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? 'SENDING...' : 'SEND MY MESSAGE'}
                </button>
              </form>
            )}
          </div>

          {/* What Happens Next - Now on RIGHT with image at top */}
          <div className="space-y-8">
            {/* Baby and parent hands image at the top */}
            <div>
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_ded95140-8b50-4155-82a1-50fd9b34f147/artifacts/hx8fu4tc_Belonging_Mum%20and%20baby.webp"
                alt="Connection and support"
                className="w-full h-[400px] object-cover rounded-3xl shadow-lg"
              />
            </div>

            <div>
              <h2
                className="text-3xl md:text-4xl mb-8"
                style={{ fontFamily: 'Cormorant Garamond, serif', color: '#815863' }}
              >
                What happens after you reach out?
              </h2>
              
              <div className="space-y-6 mb-8">
                <p className="leading-relaxed" style={{ color: '#4d6654' }}>
                  I'll reply personally within 1–2 working days.
                </p>
                <p className="leading-relaxed" style={{ color: '#4d6654' }}>
                  If it feels like the right next step, we'll arrange a Connection Call.
                </p>
                <p className="leading-relaxed" style={{ color: '#4d6654' }}>
                  Together, we'll explore what kind of support feels right for you and your family, with no pressure or expectation to commit.
                </p>
              </div>

              {/* Beige box with reassuring message only */}
              <div className="p-6 bg-[#f0e6d6] rounded-2xl mb-8">
                <p className="leading-relaxed" style={{ color: '#4d6654' }}>
                  <strong>You don't have to have everything figured out.</strong>
                </p>
                <p className="leading-relaxed mt-4" style={{ color: '#4d6654' }}>
                  Many women get in touch before they know exactly what they need.
                </p>
                <p className="leading-relaxed mt-4" style={{ color: '#4d6654' }}>
                  Whether you're looking for practical support, reassurance, or simply someone to talk things through with, you're warmly welcome to begin with a conversation.
                </p>
              </div>

              {/* Contact Info below the box */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center">
                  <div className="bg-[#f0e6d6] w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <Mail className="text-[#815863]" size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#4d6654' }}>Email</p>
                    <a
                      href="mailto:contact@soulbridgedoula.co.uk"
                      className="text-sm hover:underline"
                      style={{ color: '#815863' }}
                    >
                      contact@soulbridgedoula.co.uk
                    </a>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="bg-[#f0e6d6] w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                    <Phone className="text-[#815863]" size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#4d6654' }}>Phone</p>
                    <p className="text-sm" style={{ color: '#815863' }}>
                      Available upon request
                    </p>
                  </div>
                </div>
              </div>

              <a
                href="/contact"
                className="inline-block bg-[#815863] text-white px-8 py-3 rounded-full tracking-wider text-sm hover:bg-[#4d6654] transition-all duration-300 hover:scale-105"
              >
                BOOK A CONNECTION CALL
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <div className="py-8 px-4 bg-[#f5f4f0] text-center">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="inline-flex items-center gap-2 text-[#815863] hover:text-[#4d6654] transition-colors duration-300"
          style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
          Back to Top
        </button>
      </div>
    </div>
  );
};

export default Contact;